import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import { Loader2, X } from 'lucide-react';
import TwoFactorAuthSetup from './TwoFactorAuthSetup';
import FormField from '../../components/common/FormField';
import Modal from '../../components/common/Modal';
import { API_BASE_URL } from '../../config/config';
import {
    UserEditDTO,
    AuthClient,
    UserClient,
    InvoiceDTO,
    MemberTitleDTO,
    GenderDTO,
    LanguageDTO,
    MemberProfileDTO,
    AuditLogDTO,
} from '../../services/apiClient';
import axiosInstance from '../../services/axiosInstance';
import FileUpload from '../common/FileUpload';
import { useInvoiceApi } from '../../hooks/useInvoiceApi';
import InvoiceCard from '../common/InvoiceCard';
import handleChange from '../../utils/formUtils/handleChange';
import { useMemberTitles } from '../../hooks/useMemberTitleApi';
import { useGenders } from '../../hooks/useGenderApi';
import { useLanguages } from '../../hooks/useLanguageApi';
import { useCountries } from '../../hooks/useCountryApi';
import { useProvinces } from '../../hooks/useProvinceApi';
import { useProfilePictureSasUrl } from '../../hooks/useUserApi';
import ImageLink from '../common/ImageLink';
import AuditLogTable from '../tables/AuditLogTable';
import { useAuditLogApi } from '../../hooks/useAuditLogApi';

interface MemberProfileFormProps {
    initialValues: MemberProfileDTO;
    onSave: (member: MemberProfileDTO) => void;
    onCancel?: () => void;
    setProfilePicture?: (file: File | null) => void;
    isSubmitting: boolean;
}

const MemberProfileForm: React.FC<MemberProfileFormProps> = ({
    initialValues,
    isSubmitting,
    onSave,
    onCancel,
    setProfilePicture,
}) => {
    const [formData, setFormData] = useState(initialValues);
    const [user, setUser] = useState<UserEditDTO>();
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [twoFactorSetupComplete, setTwoFactorSetupComplete] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [emailCheckTimeout, setEmailCheckTimeout] = useState<number>();
    const [invoices, setInvoices] = useState<InvoiceDTO[]>([]);


    const [auditLogs, setAuditLogs] = useState<AuditLogDTO[]>([]);
    const [auditCurrentPage, setAuditCurrentPage] = useState(1);
    const [auditTotalPages, setAuditTotalPages] = useState(1);
    const [auditPageSize, setAuditPageSize] = useState(10);

    const authClient = new AuthClient(API_BASE_URL, axiosInstance);
    const userClient = new UserClient(API_BASE_URL, axiosInstance);

    const { searchMemberInvoices, isLoading: isLoadingInvoices } = useInvoiceApi();
    const { data: titlesData, isLoading: isLoadingTitles } = useMemberTitles();
    const { data: gendersData, isLoading: isLoadingGenders } = useGenders();
    const { data: languagesData, isLoading: isLoadingLanguages } = useLanguages();
    const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();
    const { data: countriesData, isLoading: isLoadingCountries } = useCountries();
    const { search: searchAuditLogs, isLoading: isLoadingAuditLogs } = useAuditLogApi();

    // Use TanStack Query hook for profile picture (automatically cached)
    const { data: profilePictureUrl } = useProfilePictureSasUrl(initialValues.userId);

    const FORM_SECTIONS = {
        profilePicture: {
            id: 'profile-picture',
            title: 'Profile Picture',
        },
        profile: {
            id: 'profile',
            title: 'Profile',
        },
        companyDetails: {
            id: 'company-details',
            title: 'Company Details',
        },
        physicalAddress: {
            id: 'physical-address',
            title: 'Physical Address',
        },
        courierAddress: {
            id: 'courier-address',
            title: 'Courier Address',
        },
        invoices: {
            id: 'invoices',
            title: 'Invoices',
        },
        profileHistory: {
            id: 'profile-history',
            title: 'Profile History',
        },
    } as const;

    useEffect(() => {
        // Initialize 2FA state from initial values
        if (typeof initialValues.twoFactorEnabled === 'boolean') {
            setTwoFactorEnabled(initialValues.twoFactorEnabled ?? false);
        }

        const fetchMemberInvoices = async () => {
            try {
                const response = await searchMemberInvoices(initialValues.id ?? '');
                setInvoices(response?.data?.results || []);
            }
            catch (error) {
                console.error('Error fetching invoices:', error);
            }
        };

        fetchMemberInvoices();
    }, [initialValues]);

    useEffect(() => {
        const fetchAuditLogs = async () => {
            if (initialValues.id) {
                try {
                    const response = await searchAuditLogs({
                        page: auditCurrentPage,
                        pageSize: auditPageSize,
                        sortBy: 'CreatedUtc',
                        sortDescending: true,
                        filters: {
                            tableName: 'Member',
                            recordId: initialValues.id,
                        },
                    });

                    if (response && response?.success && response?.data) {
                        setAuditLogs(response.data.results as AuditLogDTO[]);
                        setAuditTotalPages(Math.ceil(response.data.totalCount ?? 0 / auditPageSize));
                    }
                } catch (error) {
                    console.error('Error fetching audit logs:', error);
                }
            }
        };

        fetchAuditLogs();
    }, [initialValues.id, auditCurrentPage, auditPageSize]);

    useEffect(() => {
        return () => {
            if (emailCheckTimeout) {
                clearTimeout(emailCheckTimeout);
            }
        };
    }, [emailCheckTimeout]);

    const validateForm = (): boolean => {
        const formErrors: { [key: string]: string } = {};

        // Validate required fields
        if (!formData.firstName?.trim()) {
            formErrors.firstName = 'First name is required.';
        }
        if (!formData.lastName?.trim()) {
            formErrors.lastName = 'Last name is required.';
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email || !emailRegex.test(formData.email)) {
            formErrors.email = 'Valid email is required.';
        }

        if (!formData.memberTitleId) {
            formErrors.memberTitleId = 'Title is required.';
        }

        if (!formData.genderId) {
            formErrors.genderId = 'Gender is required.';
        }

        if (!formData.languageId) {
            formErrors.languageId = 'Language is required.';
        }

        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        const profilePayload: MemberProfileDTO = {
            ...formData,
            twoFactorEnabled,
        };

        try {
            await onSave(profilePayload);
        } catch (error) {
            console.error('Error saving member profile:', error);
        }
    }

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file && setProfilePicture) {
            setProfilePicture(file);
        }
    };

    const handleTwoFactorChange = async (enabled: boolean): Promise<void> => {
        setTwoFactorEnabled(enabled);
        if (enabled) {
            try {
                const response = await authClient.setupTwoFactor(
                    formData.userId ?? '',
                );
                setQrCodeUrl(response.data?.qrCodeUrl ?? '');
                setShowTwoFactorModal(true);
            } catch (error) {
                console.error('Failed to setup two-factor authentication', error);
            }
        }
    };

    const closeTwoFactorModal = (): void => {
        setShowTwoFactorModal(false);
        if (!twoFactorSetupComplete) {
            setTwoFactorEnabled(false); // Reset only if setup is not complete
        }
    };
    const handleTwoFactorVerification = async (
        code: string,
    ): Promise<boolean> => {
        try {
            const response = await authClient.verifyTwoFactor({
                userId: formData.userId,
                code,
            });

            if (response?.success) {
                setTwoFactorSetupComplete(true); // Mark setup as complete

                return true; // Verification successful
            }

            return false; // Verification failed
        } catch (error) {
            console.error('Failed to verify two-factor code', error);

            return false; // Treat error as failure
        }
    };

    const sections = Object.values(FORM_SECTIONS);

    return (
        <Form
            sections={sections}
            onSave={handleSave}
            onCancel={onCancel}
            isSubmitting={isSubmitting}
        >
            <FormSection
                id={FORM_SECTIONS.profilePicture.id}
                title={FORM_SECTIONS.profilePicture.title}
            >
                {profilePictureUrl && (
                    <div className="relative flex items-center mt-4 mb-4">
                        <div className="relative">
                            <img
                                src={profilePictureUrl}
                                alt="Profile"
                                style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                className="rounded-full"
                            />
                            <button
                                onClick={() => setFormData({ ...formData, profilePicture: '' })}
                                className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition"
                                aria-label="Remove Profile Picture"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
                <FileUpload
                    id="profilePicture"
                    title="Upload or drag and drop profile picture"
                    accept={['.png', '.jpg', '.jpeg', '.webp', '.svg']}
                    onChange={handleFileChange}
                />
            </FormSection>

            <FormSection
                id={FORM_SECTIONS.profile.id}
                title={FORM_SECTIONS.profile.title}
            >
                <FormField
                    id="memberCategory"
                    label="Membership"
                    value={formData.memberType?.title || ''}
                    onChange={() => { }}
                    disabled
                />
                <FormField
                    id="memberType"
                    label="FCA Status"
                    value={formData.memberCategory?.title || ''}
                    onChange={() => { }}
                    disabled
                />
                <FormField
                    id="memberTitleId"
                    label="Title"
                    value={formData.memberTitleId || ''}
                    type='dropdown'
                    onChange={(e) => handleChange(e, setFormData)}
                    options={[...(titlesData || []).map((t) => ({ label: t.title, value: t.id }))]}
                    isLoading={isLoadingTitles}
                    error={errors.memberTitleId}
                />
                <FormField
                    id="initials"
                    label="Initials"
                    value={formData.initials || ''}
                    placeholder="Select initials"
                    onChange={() => { }}
                    disabled
                />

                <FormField
                    id="firstName"
                    label="First Name"
                    value={formData.firstName || ''}
                    onChange={() => { }}
                    placeholder="Enter first name"
                    error={errors.firstName}
                    disabled
                />
                <FormField
                    id="lastName"
                    label="Last Name"
                    value={formData.lastName || ''}
                    onChange={() => { }}
                    placeholder="Enter last name"
                    error={errors.lastName}
                    disabled
                />
                <FormField
                    id="idNumber"
                    label="Id Number"
                    value={formData.idPassportNumber || ''}
                    onChange={() => { }}
                    placeholder="Enter ID number"
                    disabled
                />
                <FormField
                    id="genderId"
                    label="Gender"
                    type='dropdown'
                    value={formData.genderId || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Select gender"
                    error={errors.genderId}
                    options={(gendersData || []).map((g) => ({ label: g.title, value: g.id }))}
                    isLoading={isLoadingGenders}
                />
                <FormField
                    id="languageId"
                    label="Language"
                    type='dropdown'
                    value={formData.languageId || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Select language"
                    error={errors.languageId}
                    options={[...(languagesData || []).map((l) => ({ label: l.title, value: l.id }))]}
                    isLoading={isLoadingLanguages}
                />
                <FormField
                    id="birthDate"
                    label="Birth Date"
                    value={formData.dateOfBirth || ''}
                    type='date'
                    onChange={() => { }}
                    placeholder="Enter birth date"
                    disabled
                />
                <FormField
                    id="email"
                    label="Email"
                    value={formData.email || ''}
                    type='email'
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter email"
                    textCase='lowercase'
                    error={errors.email}
                />
                <FormField
                    id="phoneNumber"
                    label="Cellphone"
                    value={formData.phoneNumber || ''}
                    type='text'
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter cellphone"
                    error={errors.phoneNumber}
                />
                <FormField
                    id="landlinePhone"
                    label="Telephone"
                    value={formData.landlinePhone || ''}
                    type='text'
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter telephone"
                />

                <FormField
                    id="twoFactorEnabled"
                    label="Enable Two-Factor Authentication?"
                    type="checkbox"
                    value={twoFactorEnabled}
                    onChange={(e) =>
                        handleTwoFactorChange((e.target as HTMLInputElement).checked)
                    }
                />
            </FormSection>

            <FormSection
                id={FORM_SECTIONS.companyDetails.id}
                title={FORM_SECTIONS.companyDetails.title}
            >
                <FormField
                    id="companyName"
                    label="Company Name"
                    value={formData.companyName || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter company name"
                    error={errors.companyName}
                />
                <FormField
                    id="companyVat"
                    label="Company Vat"
                    value={formData.companyVat || ''}
                    onChange={(e) => handleChange(e, setFormData)}
                    placeholder="Enter company vat"
                    error={errors.companyVat}
                />
            </FormSection>

            <FormSection
                id={FORM_SECTIONS.physicalAddress.id}
                title={FORM_SECTIONS.physicalAddress.title}
            >
                <FormField
                    id="physicalAddress"
                    label="Street Address"
                    value={formData.physicalAddress || ''}
                    placeholder='Enter street address'
                    onChange={(e) => handleChange(e, setFormData)}
                />
                <FormField
                    id="physicalSuburb"
                    label="Suburb"
                    value={formData.physicalSuburb || ''}
                    placeholder='Enter suburb'
                    onChange={(e) => handleChange(e, setFormData)}
                />
                <FormField
                    id="physicalTown"
                    label="Town/City"
                    value={formData.physicalTown || ''}
                    placeholder='Enter town/city'
                    onChange={(e) => handleChange(e, setFormData)}
                />
                <FormField
                    id="physicalPostalCode"
                    label="Postal Code"
                    value={formData.physicalPostalCode || ''}
                    placeholder='Enter postal code'
                    onChange={(e) => handleChange(e, setFormData)}
                />
                <FormField
                    id="provinceId"
                    label="Province"
                    value={formData.provinceId || ''}
                    type='dropdown'
                    placeholder='Select province'
                    onChange={(e) => handleChange(e, setFormData)}
                    options={(provinces || []).map((p) => ({ label: p.title, value: p.id }))}
                    isLoading={isLoadingProvinces}
                />
                <FormField
                    id="countryId"
                    label="Country"
                    value={formData.countryId || ''}
                    type='dropdown'
                    placeholder='Select country'
                    onChange={(e) => handleChange(e, setFormData)}
                    options={(countriesData || []).map((c) => ({ label: c.title, value: c.id }))}
                    isLoading={isLoadingCountries}
                />
            </FormSection>

            <FormSection
                id={FORM_SECTIONS.courierAddress.id}
                title={FORM_SECTIONS.courierAddress.title}
            >
                <FormField
                    id="courierAddress"
                    label="Street Address"
                    value={formData.courierAddress || ''}
                    placeholder='Enter street address'
                    onChange={(e) => handleChange(e, setFormData)}
                />
                <FormField
                    id="courierSuburb"
                    label="Suburb"
                    value={formData.courierSuburb || ''}
                    placeholder='Enter suburb'
                    onChange={(e) => handleChange(e, setFormData)}
                />
                <FormField
                    id="courierTown"
                    label="Town/City"
                    value={formData.courierTown || ''}
                    placeholder='Enter town/city'
                    onChange={(e) => handleChange(e, setFormData)}
                />
                <FormField
                    id="courierPostalCode"
                    label="Postal Code"
                    value={formData.courierPostalCode || ''}
                    placeholder='Enter postal code'
                    onChange={(e) => handleChange(e, setFormData)}
                />
            </FormSection>

            <FormSection
                id={FORM_SECTIONS.invoices.id}
                title={FORM_SECTIONS.invoices.title}
                columns={1}
            >
                {isLoadingInvoices ? (
                    <Loader2 className="absolute right-2 h-5 w-5 text-slate-400 dark:text-slate-500 animate-spin" />
                ) : invoices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        No invoices found for this member.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {invoices.map((invoice) => (
                            <InvoiceCard
                                key={invoice.id}
                                invoice={invoice}
                            />
                        ))}
                    </div>
                )}
            </FormSection>

            <FormSection
                id={FORM_SECTIONS.profileHistory.id}
                title={FORM_SECTIONS.profileHistory.title}
            >
                <AuditLogTable
                    data={auditLogs.filter(log => log.changedBy === formData.userId)}
                    currentPage={auditCurrentPage}
                    totalPages={auditTotalPages}
                    setCurrentPage={setAuditCurrentPage}
                    pageSize={auditPageSize}
                    setPageSize={setAuditPageSize}
                    isLoading={isLoadingAuditLogs}
                />
            </FormSection>

            {/* Modal for Two-Factor Authentication */}
            <Modal
                isOpen={showTwoFactorModal}
                onClose={closeTwoFactorModal}
                title="Two-Factor Authentication Setup"
            >
                <TwoFactorAuthSetup
                    qrCodeUrl={qrCodeUrl ?? ''}
                    onVerifyCode={handleTwoFactorVerification}
                    onFinish={closeTwoFactorModal}
                />
            </Modal>
        </Form>
    );
};

export default MemberProfileForm;
